import { useMemo, useState, useEffect, Fragment } from 'react';
import "./PaginatedTable.css"

function PaginatedTable({
  columns,
  extraColumns = [],
  data = [],
  pageSize = 10,
  exportName = 'export',
  page: externalPage,
  totalPages: externalTotalPages,
  paginationLabel,
  onPageChange,
  onRowClick,
  tableClassName = '',
  flatWrapper = false
}) {
  const serverSide =
    typeof externalPage === 'number' &&
    typeof onPageChange === 'function';

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [pageInput, setPageInput] = useState('1');
  const [expandedRowId, setExpandedRowId] = useState(null);

  /* =====================
     DATA PIPELINE
  ===================== */

  const filteredData = useMemo(() => data, [data]);

  const sortedData = useMemo(() => {
    if (serverSide || !sort.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const A = a[sort.key] ?? '';
      const B = b[sort.key] ?? '';
      if (A < B) return sort.dir === 'asc' ? -1 : 1;
      if (A > B) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sort, serverSide]);

  const totalPages = serverSide
    ? externalTotalPages
    : Math.max(1, Math.ceil(sortedData.length / pageSize));

  const currentPage = serverSide ? externalPage : page;

  useEffect(() => {
    if (!serverSide && page > totalPages) setPage(1);
  }, [page, totalPages, serverSide]);

  useEffect(() => {
    // Mantener el input siempre sincronizado con la página actual
    if (!totalPages) return;
    setPageInput(String(currentPage || 1));
  }, [currentPage, totalPages]);

  const start = (currentPage - 1) * pageSize;
  const rows = serverSide
    ? data
    : sortedData.slice(start, start + pageSize);

  /* =====================
     HANDLERS
  ===================== */

  const changePage = newPage => {
    if (serverSide) onPageChange(newPage);
    else setPage(newPage);
  };

  const handlePageInputChange = e => {
    const raw = e.target.value;
    // Solo permitir dígitos para una UX más limpia
    const numeric = raw.replace(/\D/g, '');
    setPageInput(numeric);
  };

  const goToInputPage = () => {
    if (!pageInput) return;
    let next = Number(pageInput);
    if (Number.isNaN(next)) return;
    if (next < 1) next = 1;
    if (next > totalPages) next = totalPages;
    if (next === currentPage) return;
    changePage(next);
  };

  const toggleSort = key => {
    if (serverSide) return;

    setSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
    setPage(1);
  };

  /* =====================
     RENDER
  ===================== */

  return (
    <>
      {/* Toolbar */}
      {!flatWrapper ? (
        <div style={styles.toolbar}>
        {/* {!serverSide && (
          <input
            style={styles.search}
            placeholder="🔍 Buscar…"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        )} */}

        {/*
        <button
          id={`${exportName}-excel-btn`}
          style={styles.excelButton}
          onClick={exportExcel}
        >
          📑 Excel
        </button>
        */}
        </div>
      ) : null}

      {/* Empty */}
      {rows.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>🔍</div>
          <div style={styles.emptyTitle}>No hay resultados</div>
        </div>
      ) : (
        <>
          {/* ===== TABLE ===== */}
          <div
            className={['datatable-table', tableClassName].filter(Boolean).join(' ')}
            style={flatWrapper ? undefined : styles.wrapper}
          >
            <table className={['hs-table', tableClassName && `${tableClassName}__table`].filter(Boolean).join(' ')} style={styles.table}>
              <colgroup>
                {extraColumns.length > 0 && <col style={{ width: 60 }} />}
                {columns.map(col => (
                  <col
                    key={col.key}
                    style={col.width ? { width: col.width } : undefined}
                  />
                ))}
              </colgroup>

              <thead>
                <tr>
                  {extraColumns.length > 0 && (
                    <th
                      style={{
                        ...styles.th,
                        width: 60,
                        textAlign: 'center',
                        cursor: 'default'
                      }}
                    >
                      Más
                    </th>
                  )}
                  {columns.map(col => (
                    <th
                      key={col.key}
                      style={{ ...styles.th, textAlign: col.align || 'left' }}
                      onClick={() => toggleSort(col.key)}
                    >
                      {col.label}
                      {!serverSide && sort.key === col.key && (
                        <span> {sort.dir === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                  ))}
                  {/* {extraColumns.length > 0 && (
                    <th style={{ ...styles.th, width: 60, textAlign: 'center' }}>
                      Más
                    </th>
                  )} */}
                </tr>
              </thead>

              <tbody>
                {rows.map((row, i) => (
                  <Fragment key={row.id || i}>
                    <tr
                      style={{
                        ...(i % 2 ? styles.zebra : null),
                        cursor: onRowClick ? 'pointer' : 'default'
                      }}
                      onClick={() => onRowClick && onRowClick(row)}
                    >
                      {extraColumns.length > 0 && (
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              const id = row.id || i;
                              setExpandedRowId(prev => (prev === id ? null : id));
                            }}
                            className={
                              tableClassName
                                ? `${tableClassName}__expand-btn`
                                : undefined
                            }
                            style={
                              tableClassName
                                ? undefined
                                : {
                                    padding: '4px 8px',
                                    borderRadius: 999,
                                    border: '1px solid #E5E7EB',
                                    background: '#FFFFFF',
                                    fontSize: 11,
                                    cursor: 'pointer'
                                  }
                            }
                            aria-expanded={expandedRowId === (row.id || i)}
                            aria-label={
                              expandedRowId === (row.id || i)
                                ? 'Ocultar detalle'
                                : 'Ver más datos'
                            }
                          >
                            {expandedRowId === (row.id || i) ? '−' : '+'}
                          </button>
                        </td>
                      )}
                      {columns.map(col => (
                        <td key={col.key} style={styles.td}>
                          {col.render ? col.render(row) : row[col.key] ?? '—'}
                        </td>
                      ))}
                    </tr>
                    {extraColumns.length > 0 && expandedRowId === (row.id || i) && (
                      <tr>
                        <td
                          colSpan={columns.length + (extraColumns.length > 0 ? 1 : 0)}
                          className={tableClassName ? `${tableClassName}__expand-cell` : undefined}
                          style={tableClassName ? undefined : { ...styles.td, background: '#F9FAFB' }}
                        >
                          <div
                            className={tableClassName ? `${tableClassName}__expand-grid` : undefined}
                            style={
                              tableClassName
                                ? undefined
                                : {
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                    gap: 12
                                  }
                            }
                          >
                            {extraColumns.map(col => (
                              <div
                                key={col.key}
                                className={tableClassName ? `${tableClassName}__expand-card` : undefined}
                                style={
                                  tableClassName
                                    ? undefined
                                    : {
                                        fontSize: 12,
                                        padding: '8px 10px',
                                        borderRadius: 10,
                                        border: '1px solid #E5E7EB',
                                        background: '#FFFFFF'
                                      }
                                }
                              >
                                <div
                                  className={tableClassName ? `${tableClassName}__expand-card-label` : undefined}
                                  style={
                                    tableClassName
                                      ? undefined
                                      : {
                                          fontWeight: 600,
                                          color: '#6b7280',
                                          marginBottom: 4
                                        }
                                  }
                                >
                                  {col.label}
                                </div>
                                <div
                                  className={tableClassName ? `${tableClassName}__expand-card-value` : undefined}
                                  style={tableClassName ? undefined : { color: '#111827' }}
                                >
                                  {col.render
                                    ? col.render(row)
                                    : row[col.key] ?? '—'}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* ===== CARDS (mobile) ===== */}
          <div className="datatable-cards" style={styles.cards}>
            {rows.map(row => (
              <div
                key={row.id}
                style={styles.card}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map(col => (
                  <div key={col.key} style={styles.cardRow}>
                    <span style={styles.cardLabel}>{col.label}</span>
                    <span>
                      {col.render
                        ? col.render(row)
                        : row[col.key] ?? '—'}
                    </span>
                  </div>
                ))}

                {extraColumns.length > 0 && (
                  <details>
                    <summary style={{ cursor: 'pointer' }}>Más</summary>
                    {extraColumns.map(col => (
                      <div key={col.key} style={styles.cardRow}>
                        <span style={styles.cardLabel}>{col.label}</span>
                        <span>
                          {col.render
                            ? col.render(row)
                            : row[col.key] ?? '—'}
                        </span>
                      </div>
                    ))}
                  </details>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="datatable-pagination" style={styles.pagination}>
          <button
            className="datatable-pagination-button"
            style={styles.paginationButton}
            disabled={currentPage === 1}
            onClick={() => changePage(1)}
          >
            ⏮
          </button>

          <button
            className="datatable-pagination-button"
            style={styles.paginationButton}
            disabled={currentPage === 1}
            onClick={() => changePage(currentPage - 1)}
          >
            ◀
          </button>

          <span className="datatable-pagination-label" style={{ marginTop: '4px' }}>
            {paginationLabel || `Pag ${currentPage} de ${totalPages}`}
          </span>

          <div className="datatable-goto-wrapper" style={styles.gotoWrapper}>
            <input
              className="datatable-goto-input"
              value={pageInput}
              onChange={handlePageInputChange}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  goToInputPage();
                }
              }}
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder={`1–${totalPages}`}
              style={{...styles.gotoInput, width: '22px !important'}}
            />
            <button
              type="button"
              className="datatable-goto-button"
              style={styles.gotoButton}
              onClick={goToInputPage}
            >
              Ir
            </button>
          </div>

          <button
            className="datatable-pagination-button"
            style={styles.paginationButton}
            disabled={currentPage === totalPages}
            onClick={() => changePage(currentPage + 1)}
          >
            ▶
          </button>

          <button
            className="datatable-pagination-button"
            style={styles.paginationButton}
            disabled={currentPage === totalPages}
            onClick={() => changePage(totalPages)}
          >
            ⏭
          </button>
        </div>
      )}
    </>
  );
}

/* =====================
   STYLES
===================== */

const styles = {
  toolbar: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem',
    alignItems: 'center'
  },
  search: {
    padding: '7px 12px',
    borderRadius: 6,
    border: '1px solid #CBD6E2',
    fontSize: 13
  },
  excelButton: {
    marginLeft: 'auto',
    padding: '7px 15px',
    borderRadius: 6,
    border: '1px solid #CBD6E2',
    background: '#ffffff',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    position: 'absolute',
    right: '37px',
    top: '90px'
  },
  wrapper: {
    overflowX: 'visible',
    border: '1px solid #DFE3EB',
    borderRadius: 12,
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    background: '#FFFFFF'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed'
  },
  th: {
    padding: '10px 14px',
    background: '#F5F8FA',
    borderBottom: '1px solid #DFE3EB',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 700,
    color: '#7C98B6',
    textTransform: 'uppercase',
    letterSpacing: '0.07em'
  },
  td: {
    padding: '11px 14px',
    borderBottom: '1px solid #DFE3EB',
    fontSize: 13,
    background: '#FFFFFF'
  },
  zebra: { background: 'none' },

  pagination: {
    marginTop: '1rem',
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end'
  },
  paginationButton: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    cursor: 'pointer'
  },

  cards: {
    display: 'none'
  },
  card: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    background: '#fff',
    cursor: 'pointer',
    transition: 'background 0.12s ease, box-shadow 0.12s ease'
  },
  cardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0'
  },
  cardLabel: {
    fontWeight: 600,
    color: '#374151'
  },

  empty: {
    minHeight: 240,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280'
  },
  emptyIcon: { fontSize: '2rem' },
  emptyTitle: { fontWeight: 600 },
  gotoWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginLeft: 12
  },
  gotoLabel: {
    fontSize: 12,
    color: '#6b7280'
  },
  gotoInput: {
    width: 22,
    height: 22,
    padding: '6px 8px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 12,
    textAlign: 'center'
  },
  gotoButton: {
    padding: '6px 10px',
    borderRadius: 999,
    border: 'none',
    background: '#2563eb',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
  }
};
export default PaginatedTable;
